import React, { useEffect, useRef, useCallback } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { IFeatureCollectionModel } from "@/util/http/Lightning/responseType";

interface CustomerMarkerProps {
    searchResult: IFeatureCollectionModel | null;
    searchType?: string | null;
}

function getDynamicIcon(zoom: number, count?: number): google.maps.Icon {
    const size = 38; // 图标固定大小
    const viewBoxSize = 1024;
    const finalCount = count ?? "";

    // 圆形和数字的配置
    const circleRadius = 200; // 圆的半径（viewBox 单位）
    const circleFillColor = "#FFFFFF"; // 圆的填充颜色（白色）
    const circleStrokeColor = "#000000"; // 圆的描边颜色
    const circleStrokeWidth = 20; // 圆的描边宽度

    const fontSize = count ? 300 : 0; // 数字大小根据 count 是否存在而设定
    const textColor = "#000000"; // 数字颜色（黑色）
    const textStrokeColor = "#FFFFFF"; // 数字描边颜色（白色）
    const textStrokeWidth = 10; // 数字描边宽度

    const svg = `
        <svg width="${size}" height="${size}" viewBox="0 0 1024 1024"
             xmlns="http://www.w3.org/2000/svg">
            <path d="M377.9 114.1h358.3l-136.4 268 231.5 0.3-485.1 559.3 139.5-356.4H222.6z"
                  fill="#FED928" stroke="#000" stroke-width="20"/>
            ${finalCount ? `
                <circle cx="512" cy="450" r="${circleRadius}"
                        fill="${circleFillColor}"
                        stroke="${circleStrokeColor}"
                        stroke-width="${circleStrokeWidth}"/>
                <text x="512" y="480"
                      font-family="Arial"
                      font-weight="bold"
                      font-size="${fontSize}"
                      text-anchor="middle"
                      fill="${textColor}"
                      stroke="${textStrokeColor}"
                      stroke-width="${textStrokeWidth}"
                      paint-order="stroke fill"
                      dominant-baseline="middle">
                    ${finalCount}
                </text>` : ""}
        </svg>
    `;

    return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
    };
}



const CustomerMarker: React.FC<CustomerMarkerProps> = ({ searchResult, searchType }) => {
    const map = useMap();
    const allRenderedMarkersRef = useRef<google.maps.Marker[]>([]);
    const markerClustererRef = useRef<MarkerClusterer | null>(null);

    const clearAllMarkers = useCallback(() => {
        if (markerClustererRef.current) {
            markerClustererRef.current.clearMarkers();
        } else {
            allRenderedMarkersRef.current.forEach(marker => marker.setMap(null));
        }
        allRenderedMarkersRef.current = [];
        console.log("CustomerMarker: 所有自定义 Marker 已清除。");
    }, []);

    useEffect(() => {
        if (!map || searchType !== "Detected" || !searchResult?.features?.length) {
            clearAllMarkers();
            console.log("CustomerMarker: 条件不满足，不显示 Marker 或已清除 Marker。");
            return;
        }

        clearAllMarkers();

        const zoom = map.getZoom() || 10;
        const newMarkers: google.maps.Marker[] = [];

        searchResult.features.forEach(feature => {
            const geometry = feature.geometry;
            if (!geometry || !geometry.coordinates) return;

            const coordsArray = geometry.type === "Point"
                ? [geometry.coordinates as number[]]
                : geometry.coordinates as number[][];

            coordsArray.forEach(([lng, lat]) => {
                if (typeof lat === "number" && typeof lng === "number") {
                    const marker = new google.maps.Marker({
                        position: { lat, lng },
                        icon: getDynamicIcon(zoom),
                        title: feature.properties?.title || "",
                    });
                    newMarkers.push(marker);
                }
            });
        });

        allRenderedMarkersRef.current = newMarkers;

        markerClustererRef.current = new MarkerClusterer({
            map,
            markers: newMarkers,
            renderer: {
                render: ({ count, position }) => {
                    return new google.maps.Marker({
                        position,
                        icon: getDynamicIcon(map.getZoom() || 10, count),
                        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                    });
                }
            }
        });

        console.log(`CustomerMarker: 已处理 ${newMarkers.length} 个自定义 Marker 进行聚合。`);

        return () => {
            console.log("CustomerMarker: 组件卸载或依赖项变化，清除所有 Marker。");
            clearAllMarkers();
            if (markerClustererRef.current) {
                markerClustererRef.current.setMap(null);
                markerClustererRef.current = null;
            }
        };
    }, [map, searchResult, searchType, clearAllMarkers]);

    // 监听地图缩放变化以动态调整 Marker & Cluster 图标
    useEffect(() => {
        if (!map) return;

        const handleZoomChanged = () => {
            const zoom = map.getZoom() || 10;

            // 更新 Marker 图标
            allRenderedMarkersRef.current.forEach(marker => {
                marker.setIcon(getDynamicIcon(zoom));
            });

            // 重新设置 Clusterer（强制刷新图标）
            if (markerClustererRef.current) {
                const currentMarkers = allRenderedMarkersRef.current;
                markerClustererRef.current.clearMarkers();
                markerClustererRef.current.addMarkers(currentMarkers);
            }
        };

        const listener = map.addListener("zoom_changed", handleZoomChanged);
        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [map]);

    return null;
};

export default React.memo(CustomerMarker);
